import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  createCompany,
  getCompany,
  listMembers,
  updateCompany,
  uploadRoster,
  type Company,
  type Member,
  type RosterUploadResult,
} from "../../services/companies-api";
import { uploadImage } from "../../services/uploads-api";
import { getAppCatalog, normalizeTag, type AppCatalog } from "../../services/app-catalog-api";
import { TagPicker } from "../../components/TagPicker";
import { PackagePicker } from "../../components/PackagePicker";
import { CompanyMembersTable } from "./CompanyMembers";

/** FR-003/FR-006/FR-030/FR-041: create a company, or manage an existing one's apps, roster,
 * logo, and instant-answer-reveal setting. */
export function CompanyDetailPage() {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const isNew = companyId === "new";
  const navigate = useNavigate();

  const [company, setCompany] = useState<Company | null>(null);
  const [name, setName] = useState("");
  const [apps, setApps] = useState<string[]>([]);
  const [packages, setPackages] = useState<string[]>([]);
  const [catalog, setCatalog] = useState<AppCatalog | null>(null);
  /** See QuestionForm: "failed" and "still loading" both read as null, but say different things. */
  const [catalogFailed, setCatalogFailed] = useState(false);
  /** FR-070: apps on this company that aren't Base apps. Held outside `apps` (the array that
   * gets saved), so navigating away or pressing Save apps cannot persist them. */
  const [rejectedApps, setRejectedApps] = useState<string[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [rosterResult, setRosterResult] = useState<RosterUploadResult | null>(null);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  async function loadMembers(id: string) {
    setMembers(await listMembers(id));
  }

  /** The apps exactly as the server last reported them. Kept separate from `apps` (the editable
   * set) so that toggling instant-reveal or uploading a logo — both of which refresh `company` —
   * can't quietly discard app edits the CO Member hasn't saved yet. */
  const [serverApps, setServerApps] = useState<string[] | null>(null);

  useEffect(() => {
    if (isNew || !companyId) return;
    getCompany(companyId).then((c) => {
      setCompany(c);
      setPackages(c.purchasedPackages);
      setServerApps(c.purchasedSingularApps);
    });
    loadMembers(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, isNew]);

  useEffect(() => {
    getAppCatalog()
      .then(setCatalog)
      .catch(() => {
        setCatalog(null);
        setCatalogFailed(true);
      });
  }, []);

  /** FR-070: once both the company and the catalogue are in hand, canonicalise what's stored —
   * "WORKFLOW" becomes "Workflow" — and move anything matching no Base app into the red rejected
   * list instead of leaving it in the set that gets saved. Existing bad tags ("Digital
   * Workplace", "employee") therefore surface for the CO Member to drop; they'll reassign by
   * hand. Re-runs after a save, which is what makes the red chips disappear once they're gone. */
  useEffect(() => {
    if (!catalog || !serverApps) return;
    const valid: string[] = [];
    const refused: string[] = [];
    for (const app of serverApps) {
      const canonical = normalizeTag(app, catalog.allApps);
      if (canonical) {
        if (!valid.includes(canonical)) valid.push(canonical);
      } else if (!refused.includes(app)) {
        refused.push(app);
      }
    }
    setApps(valid);
    setRejectedApps(refused);
  }, [catalog, serverApps]);

  async function handleCreate() {
    setSaving(true);
    try {
      const created = await createCompany({
        name,
        purchasedPackages: packages,
        purchasedSingularApps: apps,
      });
      navigate(`/co-member/companies/${created.id}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveApps() {
    if (!company) return;
    setSaving(true);
    try {
      const updated = await updateCompany(company.id, {
        purchasedPackages: packages,
        purchasedSingularApps: apps,
      });
      setCompany(updated);
      setPackages(updated.purchasedPackages);
      setServerApps(updated.purchasedSingularApps);
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleInstantReveal() {
    if (!company) return;
    const updated = await updateCompany(company.id, { instantAnswerReveal: !company.instantAnswerReveal });
    setCompany(updated);
  }

  async function handleLogoUpload(file: File) {
    if (!company) return;
    setLogoUploading(true);
    try {
      const { key } = await uploadImage(file);
      const updated = await updateCompany(company.id, { logoKey: key });
      setCompany(updated);
    } finally {
      setLogoUploading(false);
    }
  }

  async function handleRosterUpload(file: File) {
    if (!company) return;
    const result = await uploadRoster(company.id, file);
    setRosterResult(result);
    await loadMembers(company.id);
  }

  if (isNew) {
    return (
      <div className="p-6">
        <h1 className="mb-4 font-sans text-xl text-ink">{t("companies.createTitle", "Create Company")}</h1>
        <div className="flex max-w-md flex-col gap-4">
          <label className="flex flex-col gap-1 text-sm text-body">
            Company name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded border border-surface-alt px-3 py-2"
            />
          </label>
          <div className="flex flex-col gap-2">
            <span className="text-sm text-body">{t("companies.packagesPurchased", "Packages purchased")}</span>
            <PackagePicker catalog={catalog} selected={packages} onChange={setPackages} />
          </div>
          <TagPicker
            label={`${t("companies.individualApps", "Individual apps")} (${t("companies.individualAppsHint", "bought outside a package")})`}
            inputId="new-company-apps"
            placeholder={t("companies.searchApps", "Search Base apps…")}
            value={apps}
            onChange={setApps}
            rejected={rejectedApps}
            onRejectedChange={setRejectedApps}
            catalog={catalog}
            loadFailed={catalogFailed}
          />
          <button
            onClick={handleCreate}
            disabled={saving || !name.trim()}
            className="self-start rounded bg-brand px-4 py-2 font-sans text-surface disabled:opacity-50"
          >
            {saving ? t("common.creating", "Creating…") : t("companies.createButton", "Create company")}
          </button>
        </div>
      </div>
    );
  }

  if (!company) return <div className="p-6 text-body">Loading…</div>;

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-sans text-xl text-ink">{company.name}</h1>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/co-member/companies/${company.id}/submissions`)}
            className="rounded bg-surface-alt px-4 py-2 font-sans text-ink"
          >
            Submissions
          </button>
          <button
            onClick={() => navigate(`/co-member/companies/${company.id}/grading`)}
            className="rounded bg-surface-alt px-4 py-2 font-sans text-ink"
          >
            Grading queue
          </button>
          <button
            onClick={() => navigate(`/co-member/companies/${company.id}/assignments/new`)}
            className="rounded bg-brand px-4 py-2 font-sans text-surface"
          >
            + Assign a test
          </button>
        </div>
      </div>
      <p className="mb-4 text-sm text-body">
        Sign-in URL: <code>/company/{company.slug}/login</code>
      </p>

      <section className="mb-6 flex max-w-3xl flex-col gap-3">
        <h2 className="font-sans text-lg text-ink">Base apps</h2>

        <div className="flex flex-col gap-2">
          <span className="text-sm text-body">{t("companies.packagesPurchased", "Packages purchased")}</span>
          <PackagePicker catalog={catalog} selected={packages} onChange={setPackages} />
        </div>

        <TagPicker
          label={`${t("companies.individualApps", "Individual apps")} (${t("companies.individualAppsHint", "bought outside a package")})`}
          inputId="company-apps"
          placeholder={t("companies.searchApps", "Search Base apps…")}
          value={apps}
          onChange={setApps}
          rejected={rejectedApps}
          onRejectedChange={setRejectedApps}
          catalog={catalog}
          loadFailed={catalogFailed}
        />

        {/* FR-064: the derived set is what FR-004 actually matches questions against, so it's
            worth showing — otherwise "why is this question eligible?" is unanswerable from the UI.
            Read-only: it's computed server-side from the two pickers above. */}
        <details className="text-sm text-body">
          <summary className="cursor-pointer">
            {t("companies.testableApps", "Apps this company can be tested on")} ({company.apps.length})
          </summary>
          <p className="mt-1 leading-relaxed">
            {company.apps.join(", ") || "None yet — pick a package or an individual app above."}
          </p>
          <p className="mt-1 text-xs">
            Derived from the packages and individual apps above, plus the apps every company gets
            for free ({(catalog?.alwaysFreeApps ?? []).join(", ")}).
          </p>
        </details>

        <button
          onClick={handleSaveApps}
          disabled={saving}
          className="self-start rounded bg-brand px-3 py-2 text-sm text-surface disabled:opacity-50"
        >
          {t("companies.saveApps", "Save apps")}
        </button>
      </section>

      <section className="mb-6 flex flex-col gap-2">
        <h2 className="font-sans text-lg text-ink">Logo</h2>
        {company.logoKey && (
          <img src={`/api/uploads/${company.logoKey}`} alt="Company logo" className="h-12 w-auto" />
        )}
        <input
          type="file"
          accept="image/*"
          aria-label="Company logo file"
          disabled={logoUploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleLogoUpload(file);
          }}
          className="text-sm"
        />
      </section>

      <section className="mb-6 flex items-center gap-2">
        <label className="flex items-center gap-2 text-sm text-body">
          <input
            type="checkbox"
            checked={company.instantAnswerReveal}
            onChange={handleToggleInstantReveal}
          />
          Instantly reveal answer explanations after each member submits (FR-030)
        </label>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="font-sans text-lg text-ink">Roster</h2>
        {/* FR-049: all seven columns, with the required three called out — a CO Member building
            the CSV by hand needs to know which ones they can safely leave blank. */}
        <div className="text-sm text-body">
          <p>
            CSV with columns:{" "}
            <code>
              Full Name, Email, Department, Password, Position Type, System Manager, Takes Test
            </code>
          </p>
          <p className="mt-1">
            <strong>Full Name</strong>, <strong>Email</strong> and <strong>Password</strong> are
            required. Leave the rest blank to accept the defaults: Department ={" "}
            <em>{company.name}</em>, Position Type = <em>Employee</em>, System Manager ={" "}
            <em>FALSE</em>, Takes Test = <em>TRUE</em>.
          </p>
          <p className="mt-1">
            Position Type accepts <code>Employee</code>, <code>Manager</code> or <code>BOD</code>.
            System Manager and Takes Test accept <code>TRUE</code>/<code>FALSE</code> (or Yes/No,
            1/0).
          </p>
        </div>
        <input
          type="file"
          accept=".csv,text/csv"
          aria-label="Roster CSV file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleRosterUpload(file);
          }}
          className="text-sm"
        />
        {rosterResult && (
          <p className="text-sm text-body">
            Created {rosterResult.created} member(s).
            {rosterResult.rejectedRows.length > 0 &&
              ` Rejected ${rosterResult.rejectedRows.length} row(s): ${rosterResult.rejectedRows
                .map((r) => `row ${r.row}: ${r.reason}`)
                .join("; ")}`}
          </p>
        )}
        <CompanyMembersTable companyId={company.id} members={members} onChanged={() => loadMembers(company.id)} />
      </section>
    </div>
  );
}
